import { useState, type ChangeEvent } from 'react'
import {
  api,
  type AccountAttachmentStoreView,
  type AccountErpView,
  type AttachmentStore,
} from '../lib/api.ts'
import {
  attachmentsDatabaseOf,
  attachmentsNeedPassword,
  attachmentsTarget,
  connectionWithAttachments,
  receitasSaveOrder,
  receitasTest,
  type ReceitasStep,
  type AttachmentsDatabaseForm,
} from '../lib/attachmentsDatabase.ts'
import { ATTACHMENT_STORE_LABEL } from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { EntityForm, Field } from './EntityForm.tsx'
import { Input } from './Input.tsx'
import { Select } from './Select.tsx'
import { useToast } from './Toast.tsx'

/**
 * Where this account's prescriptions are read from (#639), on the account's
 * Conexão tab below the ERP connection and saved apart from it.
 *
 * One choice and what it needs (#689): the ERP's attachments database, with
 * its path and host, or a Drive folder, with its link. The attachments
 * database used to sit among the connection's fields and the choice in a
 * section of its own; the two are one question. The database is still saved
 * in the connection's row, read with its user and password, so naming another
 * one asks for that password again, as the server does.
 *
 * "Testar" reaches only where the prescriptions are: a Drive folder is checked
 * without the pharmacy's Firebird, so it can be confirmed shared before the
 * ERP is reachable from here, and with the integration off. An attachments
 * database typed and not saved yet is tried through the connection, which
 * dials both of its databases.
 *
 * Choosing a Drive folder empties an attachments database the connection
 * still names (`attachmentsTarget`), and says so before it is saved.
 */
export function AccountAttachmentStore({
  accountId,
  erpView,
  storeView,
  onErpSaved,
  onStoreSaved,
}: {
  accountId: string
  erpView: AccountErpView
  storeView: AccountAttachmentStoreView
  onErpSaved: (next: AccountErpView) => void
  onStoreSaved: (next: AccountAttachmentStoreView) => void
}) {
  const [store, setStore] = useState<AttachmentStore>(storeView.store)
  const [driveFolderId, setDriveFolderId] = useState(storeView.driveFolderId ?? '')
  const [attachments, setAttachments] = useState<AttachmentsDatabaseForm>(() =>
    attachmentsDatabaseOf(erpView),
  )
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const toast = useToast()

  const storeDirty =
    store !== storeView.store ||
    (store === 'google_drive' && driveFolderId.trim() !== (storeView.driveFolderId ?? ''))
  // What the attachments database becomes on save; undefined leaves it.
  const target = attachmentsTarget(erpView, store, attachments)
  const attachmentsDirty = target !== undefined
  const asksPassword = target !== undefined && attachmentsNeedPassword(erpView, target)

  async function save() {
    setSaving(true)
    const draft =
      target === undefined ? undefined : connectionWithAttachments(erpView, target, password)
    const done: ReceitasStep[] = []
    try {
      for (const step of receitasSaveOrder(store, draft !== undefined, storeDirty)) {
        if (step === 'connection' && draft !== undefined) {
          const saved = await api.saveAccountErp(accountId, draft)
          onErpSaved(saved)
          setAttachments(attachmentsDatabaseOf(saved))
          setPassword('')
        } else if (step === 'store') {
          const saved = await api.saveAccountAttachmentStore(accountId, { store, driveFolderId })
          onStoreSaved(saved)
          setStore(saved.store)
          setDriveFolderId(saved.driveFolderId ?? '')
        }
        done.push(step)
      }
      toast.show('Origem das receitas salva', { tone: 'success' })
    } catch (cause) {
      const reason = cause instanceof Error ? cause.message : 'falha ao salvar'
      toast.show(halfSaved(done, reason), { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function test() {
    setTesting(true)
    const what = store === 'google_drive' ? 'A pasta do Drive' : 'O banco de anexos'
    try {
      const plan = receitasTest(
        erpView,
        { store, driveFolderId },
        attachments,
        password,
        storeDirty,
      )
      if (plan.kind === 'nothing') {
        toast.show(plan.reason, { tone: 'error' })
        return
      }
      const result =
        plan.kind === 'connection'
          ? await api.testAccountErp(accountId, plan.draft)
          : await api.testAccountAttachmentStore(accountId, plan.draft)
      if (result.ok) toast.show(`${what} respondeu em ${result.ms} ms`, { tone: 'success' })
      else toast.show(`${what} não respondeu: ${result.error ?? 'falhou'}`, { tone: 'error' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao testar', { tone: 'error' })
    } finally {
      setTesting(false)
    }
  }

  const label = ATTACHMENT_STORE_LABEL[storeView.store]
  return (
    <EntityForm
      title="Receitas"
      description={
        storeView.setBy === null ? `${label} (padrão, nada escolhido ainda)` : `Vêm de: ${label}`
      }
      onSave={() => void save()}
      saving={saving}
      dirty={storeDirty || attachmentsDirty}
    >
      <Select
        label="As receitas desta conta vêm de"
        value={store}
        onChange={(event: ChangeEvent<HTMLSelectElement>) =>
          setStore(event.target.value as AttachmentStore)
        }
      >
        {(Object.keys(ATTACHMENT_STORE_LABEL) as AttachmentStore[]).map((one) => (
          <option key={one} value={one}>
            {ATTACHMENT_STORE_LABEL[one]}
          </option>
        ))}
      </Select>
      {store === 'erp_database' ? (
        <AttachmentsDatabaseFields
          view={erpView}
          form={attachments}
          onChange={setAttachments}
          password={asksPassword ? password : undefined}
          onPassword={setPassword}
        />
      ) : (
        <DriveFolderField
          driveFolderId={driveFolderId}
          onChange={setDriveFolderId}
          driveServiceAccount={storeView.driveServiceAccount}
          clearsAttachments={attachmentsDirty ? attachmentsDatabaseOf(erpView).database : undefined}
        />
      )}
      {storeView.setBy !== null && storeView.setAt !== null && (
        <Field
          label="Salva por"
          value={`${storeView.setBy} em ${new Date(storeView.setAt).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}`}
        />
      )}
      <div className="flex flex-wrap gap-2 md:col-span-2">
        <Button
          type="button"
          variant="secondary"
          tone="neutral"
          size="small"
          disabled={testing}
          onClick={() => void test()}
        >
          {testing ? 'Testando…' : 'Testar origem das receitas'}
        </Button>
      </div>
    </EntityForm>
  )
}

/** A save that stopped half way, said as such: what was saved, and what was not. */
export function halfSaved(done: readonly ReceitasStep[], reason: string): string {
  if (done.includes('connection'))
    return `Banco de anexos salvo, mas a origem das receitas não: ${reason}`
  if (done.includes('store'))
    return `Origem das receitas salva, mas o banco de anexos não: ${reason}`
  return reason
}

/**
 * The attachments database's fields, or why there are none here.
 *
 * `password` is the field's value when one has to be typed, undefined when
 * the stored one still goes where it went.
 */
export function AttachmentsDatabaseFields({
  view,
  form,
  onChange,
  password,
  onPassword,
}: {
  view: AccountErpView
  form: AttachmentsDatabaseForm
  onChange: (next: AttachmentsDatabaseForm) => void
  password: string | undefined
  onPassword: (typed: string) => void
}) {
  if (view.connection === null) {
    return (
      <p className="text-tSmall text-text-weak self-end">
        {view.source === 'environment'
          ? 'Esta conta lê o ERP pelas variáveis ERP_* do ambiente, e o banco de anexos também (ERP_ATTACHMENTS_DATABASE).'
          : 'Salve a conexão com o ERP acima para informar o banco de anexos dela.'}
      </p>
    )
  }
  return (
    <>
      <Field
        label="Banco de anexos"
        value={form.database}
        editable
        onChange={(database) => onChange({ ...form, database })}
        helperText="Caminho ou alias do banco de anexos, como o servidor do ERP o expõe. É lido com o usuário e a senha da conexão com o ERP."
      />
      <Field
        label="Host do banco de anexos"
        value={form.host}
        editable
        onChange={(host) => onChange({ ...form, host })}
        helperText="Em branco: o mesmo host do banco principal."
      />
      {password !== undefined && (
        <Input
          label="Senha do ERP"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => onPassword(event.target.value)}
          helperText="Para mudar o banco de anexos, digite de novo a senha da conexão com o ERP."
        />
      )}
    </>
  )
}

/**
 * The folder's link, and, when the connection still names an attachments
 * database, that saving with a folder empties it.
 */
export function DriveFolderField({
  driveFolderId,
  onChange,
  driveServiceAccount,
  clearsAttachments,
}: {
  driveFolderId: string
  onChange: (typed: string) => void
  driveServiceAccount: string | null
  /** The attachments database saving would empty, if any. */
  clearsAttachments: string | undefined
}) {
  return (
    <>
      <Field
        label="Pasta do Google Drive"
        value={driveFolderId}
        editable
        wide
        onChange={onChange}
        helperText={<DriveFolderHelp driveServiceAccount={driveServiceAccount} />}
      />
      {clearsAttachments !== undefined && (
        <p className="text-tSmall text-text-warning md:col-span-2">
          {`A conexão com o ERP ainda guarda o banco de anexos ${clearsAttachments}. Com a pasta escolhida ele não é lido, e ao salvar é apagado da conexão.`}
        </p>
      )}
    </>
  )
}

/** Whom the folder must be shared with, or why no folder can be read here. */
export function DriveFolderHelp({ driveServiceAccount }: { driveServiceAccount: string | null }) {
  if (driveServiceAccount === null) {
    return (
      <span className="text-text-error">
        GOOGLE_SERVICE_ACCOUNT_KEY não está configurada neste ambiente: a pasta não pode ser lida.
      </span>
    )
  }
  return (
    <>
      Cole o link da pasta. Ela precisa estar compartilhada com{' '}
      <strong>{driveServiceAccount}</strong>, e cada arquivo leva no nome o número da requisição —
      ou o do orçamento, enquanto não houver requisição.
    </>
  )
}
